import React, { useState } from 'react';
import { MyMedication } from './types';
import { MyMedicationForm } from './MyMedicationForm';
import { isMedicationActive, getTodayLocal } from './utils/dateHelpers';
import { MedicationCalendar } from './MedicationCalendar';
import { MedicationTracker } from './components/MedicationTracker';
import { MedicationHistoryView } from './components/MedicationHistoryView';
import { SupplementHistoryView } from './components/SupplementHistoryView';

interface MyMedicationsProps {
  myMedications: MyMedication[];
  onAdd: (medication: MyMedication) => void;
  onUpdate: (id: string, medication: MyMedication) => void;
  onDelete: (id: string) => void;
  onNavigate?: (section: string) => void;
  userId?: string;
}

type ViewType = 'today' | 'treatments' | 'calendar';

const rem = (px: number) => `${px / 16}rem`;

const fontSize = {
  xs: rem(12),
  sm: rem(14),
  base: rem(16),
  lg: rem(18),
  xl: rem(20),
  '2xl': rem(24)
};

export const MyMedications: React.FC<MyMedicationsProps> = ({
  myMedications,
  onAdd,
  onUpdate,
  onDelete,
  onNavigate,
  userId
}) => {
  const [currentView, setCurrentView] = useState<ViewType>('today');
  const [showForm, setShowForm] = useState(false);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [categoryFilter, setCategoryFilter] = useState<'ALL' | 'LEK' | 'SUPLEMENT'>('LEK');
  const [listFilter, setListFilter] = useState<'permanent' | 'temporary' | 'all'>('all');
  const [showHistory, setShowHistory] = useState(false);

  // Filtruj po kategorii - aktywność z dat, nie z checkboxa
  const medicationsByCategory = categoryFilter === 'ALL'
    ? myMedications
    : myMedications.filter(m => m.category === categoryFilter);
  const permanentMedications = medicationsByCategory.filter(m => m.type === 'permanent' && isMedicationActive(m));
  const temporaryMedications = medicationsByCategory.filter(m =>
    (m.type === 'temporary' || m.type === 'tapering' || m.type === 'one-time') && isMedicationActive(m)
  );
  const allActiveMedications = medicationsByCategory.filter(m => isMedicationActive(m));
  const inactiveMedications = medicationsByCategory.filter(m => !isMedicationActive(m));
  const allActiveMedicationsUnfiltered = myMedications.filter(m => isMedicationActive(m));

  const handleSubmit = (data: MyMedication) => {
    if (editingId) {
      onUpdate(editingId, { ...data, id: editingId });
      setEditingId(null);
    } else {
      onAdd(data);
    }
    setShowForm(false);
  };

  const handleEdit = (medication: MyMedication) => {
    setEditingId(medication.id);
    setShowForm(true);
  };

  const handleCancel = () => {
    setShowForm(false);
    setEditingId(null);
  };

  const handleDelete = (id: string) => {
    if (window.confirm('Czy na pewno chcesz usunąć ten plan leczenia?')) {
      onDelete(id);
    }
  };

  const handleEndTreatment = (medication: MyMedication) => {
    const today = getTodayLocal();
    if (medication.type === 'tapering' && medication.taperingSchedules?.length) {
      const schedules = [...medication.taperingSchedules];
      const last = schedules[schedules.length - 1];
      schedules[schedules.length - 1] = { ...last, endDate: today };
      onUpdate(medication.id, { ...medication, taperingSchedules: schedules, endDate: today });
    } else {
      onUpdate(medication.id, { ...medication, endDate: today });
    }
  };

  const renderMedicationCard = (medication: MyMedication) => {
    const getDetails = () => {
      if (medication.type === 'permanent') {
        return `${medication.permanentDosage} szt. - ${medication.permanentFrequency}`;
      } else if (medication.type === 'temporary') {
        const endDate = medication.endDate
          ? new Date(medication.endDate + 'T12:00:00').toLocaleDateString('pl-PL')
          : 'brak daty zakończenia';
        return `${medication.temporaryDosage} szt. - ${medication.temporaryFrequency} przez ${medication.temporaryDuration} dni (do ${endDate})`;
      } else if (medication.type === 'tapering') {
        const schedules = medication.taperingSchedules || [];
        return `Schodzenie z dawki: ${schedules.length} etap(ów)`;
      } else if (medication.type === 'one-time') {
        const date = medication.oneTimeDate
          ? new Date(medication.oneTimeDate + 'T12:00:00').toLocaleDateString('pl-PL')
          : 'brak daty';
        return `${medication.oneTimeDosage} szt. - ${date}`;
      }
      return '';
    };

    return (
      <div
        key={medication.id}
        style={{
          padding: rem(20),
          marginBottom: rem(16),
          background: '#FFFFFF',
          border: '1px solid #E5E5E5',
          borderRadius: rem(24),
          boxShadow: '0 1px 2px rgba(0,0,0,0.05)'
        }}
      >
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
          <div style={{ flex: 1 }}>
            <h3 style={{
              margin: 0,
              marginBottom: rem(12),
              fontSize: fontSize.lg,
              fontWeight: 600,
              color: '#1A1A1A'
            }}>
              {medication.name}
            </h3>
            <div style={{
              fontSize: fontSize.sm,
              color: '#666666',
              marginBottom: rem(8),
              display: 'flex',
              alignItems: 'center',
              gap: rem(6)
            }}>
              <strong>Typ:</strong>
              {medication.type === 'permanent' && (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                  </svg>
                  <span>Na stałe</span>
                </>
              )}
              {medication.type === 'temporary' && (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                  </svg>
                  <span>Czasowe</span>
                </>
              )}
              {medication.type === 'tapering' && (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 7h8m0 0v8m0-8l-8 8-4-4-6 6" />
                  </svg>
                  <span>Schodzenie z dawki</span>
                </>
              )}
              {medication.type === 'one-time' && (
                <>
                  <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
                  </svg>
                  <span>Jednorazowe</span>
                </>
              )}
            </div>
            <div style={{ fontSize: fontSize.sm, color: '#666666', marginBottom: rem(8) }}>
              <strong>Dawkowanie:</strong> {getDetails()}
            </div>
            <div style={{ fontSize: fontSize.sm, color: '#666666', marginBottom: rem(8) }}>
              <strong>Data rozpoczęcia:</strong> {new Date(medication.startDate + 'T12:00:00').toLocaleDateString('pl-PL')}
            </div>
            {medication.endDate && (
              <div style={{ fontSize: fontSize.sm, color: '#666666', marginBottom: rem(8) }}>
                <strong>Data zakończenia:</strong> {new Date(medication.endDate + 'T12:00:00').toLocaleDateString('pl-PL')}
              </div>
            )}
            {medication.takingInstructions && (
              <div style={{ fontSize: fontSize.sm, color: '#666666', marginBottom: rem(8) }}>
                <strong>Przyjmowanie:</strong>{' '}
                {medication.takingInstructions === 'with_meal' ? 'W trakcie posiłku' :
                  medication.takingInstructions === 'before_meal' ? 'Przed posiłkiem' :
                  medication.takingInstructions === 'after_meal' ? 'Po posiłku' :
                  medication.takingInstructions === 'empty_stomach' ? 'Na czczo' : ''}
              </div>
            )}
            {medication.notes && (
              <div style={{ fontSize: fontSize.sm, color: '#666666', marginTop: rem(8) }}>
                <strong>Notatki:</strong> {medication.notes}
              </div>
            )}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: rem(8), marginLeft: rem(16) }}>
            {isMedicationActive(medication) && (
              <button
                onClick={() => handleEndTreatment(medication)}
                style={{
                  padding: `${rem(8)} ${rem(16)}`,
                  background: '#F59E0B',
                  color: 'white',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(6)
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Zakończ leczenie
              </button>
            )}
            <button
              onClick={() => handleEdit(medication)}
              style={{
                padding: `${rem(8)} ${rem(16)}`,
                background: '#2196F3',
                color: 'white',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: rem(6)
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
              </svg>
              Edytuj
            </button>
            <button
              onClick={() => handleDelete(medication.id)}
              style={{
                padding: `${rem(8)} ${rem(16)}`,
                background: '#EF4444',
                color: 'white',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: 600,
                display: 'flex',
                alignItems: 'center',
                gap: rem(6)
              }}
            >
              <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
              </svg>
              Usuń
            </button>
          </div>
        </div>
      </div>
    );
  };

  // Formularz
  if (showForm) {
    const editingMedication = editingId
      ? myMedications.find(m => m.id === editingId)
      : undefined;

    return (
      <div style={{ maxWidth: '1400px', margin: '0 auto' }}>
        <MyMedicationForm
          onSubmit={handleSubmit}
          initialData={editingMedication}
          onCancel={handleCancel}
        />
      </div>
    );
  }

  return (
    <div style={{ maxWidth: '1400px', margin: '0 auto', position: 'relative' }}>
      {/* Tab Navigation */}
      <div style={{
        display: 'flex',
        gap: rem(8),
        marginBottom: rem(32),
        flexWrap: 'wrap'
      }}>
        {/* Dzisiaj */}
        <button
          onClick={() => setCurrentView('today')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(12),
            padding: `${rem(12)} ${rem(24)}`,
            background: currentView === 'today'
              ? 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
              : '#FFFFFF',
            color: currentView === 'today' ? '#FFFFFF' : '#666666',
            border: currentView === 'today' ? 'none' : '1px solid #E5E5E5',
            borderRadius: rem(24),
            cursor: 'pointer',
            fontSize: fontSize.base,
            fontWeight: 600,
            boxShadow: currentView === 'today' ? '0 2px 8px rgba(107, 127, 255, 0.3)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          <div style={{
            width: rem(40),
            height: rem(40),
            borderRadius: rem(12),
            background: currentView === 'today' ? 'rgba(255,255,255,0.2)' : '#E0E7FF',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="20" height="20" fill="none" stroke={currentView === 'today' ? '#FFFFFF' : '#6366F1'} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
            </svg>
          </div>
          <span>Dzisiaj</span>
        </button>

        {/* Moje leczenie */}
        <button
          onClick={() => setCurrentView('treatments')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(12),
            padding: `${rem(12)} ${rem(24)}`,
            background: currentView === 'treatments'
              ? 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
              : '#FFFFFF',
            color: currentView === 'treatments' ? '#FFFFFF' : '#666666',
            border: currentView === 'treatments' ? 'none' : '1px solid #E5E5E5',
            borderRadius: rem(24),
            cursor: 'pointer',
            fontSize: fontSize.base,
            fontWeight: 600,
            boxShadow: currentView === 'treatments' ? '0 2px 8px rgba(107, 127, 255, 0.3)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          <div style={{
            width: rem(40),
            height: rem(40),
            borderRadius: rem(12),
            background: currentView === 'treatments' ? 'rgba(255,255,255,0.2)' : '#FEF3C7',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="20" height="20" fill="none" stroke={currentView === 'treatments' ? '#FFFFFF' : '#F59E0B'} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19.428 15.428a2 2 0 00-1.022-.547l-2.387-.477a6 6 0 00-3.86.517l-.318.158a6 6 0 01-3.86.517L6.05 15.21a2 2 0 00-1.806.547M8 4h8l-1 1v5.172a2 2 0 00.586 1.414l5 5c1.26 1.26.367 3.414-1.415 3.414H4.828c-1.782 0-2.674-2.154-1.414-3.414l5-5A2 2 0 009 10.172V5L8 4z" />
            </svg>
          </div>
          <span>Moje leczenie</span>
        </button>

        {/* Kalendarz */}
        <button
          onClick={() => setCurrentView('calendar')}
          style={{
            display: 'flex',
            alignItems: 'center',
            gap: rem(12),
            padding: `${rem(12)} ${rem(24)}`,
            background: currentView === 'calendar'
              ? 'linear-gradient(to right, #6B7FFF, #8B5CF6)'
              : '#FFFFFF',
            color: currentView === 'calendar' ? '#FFFFFF' : '#666666',
            border: currentView === 'calendar' ? 'none' : '1px solid #E5E5E5',
            borderRadius: rem(24),
            cursor: 'pointer',
            fontSize: fontSize.base,
            fontWeight: 600,
            boxShadow: currentView === 'calendar' ? '0 2px 8px rgba(107, 127, 255, 0.3)' : 'none',
            transition: 'all 0.2s'
          }}
        >
          <div style={{
            width: rem(40),
            height: rem(40),
            borderRadius: rem(12),
            background: currentView === 'calendar' ? 'rgba(255,255,255,0.2)' : '#D1FAE5',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center'
          }}>
            <svg width="20" height="20" fill="none" stroke={currentView === 'calendar' ? '#FFFFFF' : '#10B981'} viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v12a2 2 0 002 2h10a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
            </svg>
          </div>
          <span>Kalendarz</span>
        </button>
      </div>

      {/* Content */}
      {currentView === 'today' && (
        <MedicationTracker myMedications={allActiveMedicationsUnfiltered} />
      )}

      {currentView === 'calendar' && (
        <MedicationCalendar
          myMedications={myMedications}
          onScheduleGenerated={() => {}}
        />
      )}

      {currentView === 'treatments' && (
        <>
          {/* Podział: + Dodaj leczenie | Leki | Suplementy */}
          <div style={{
            display: 'flex',
            gap: rem(8),
            marginBottom: rem(24),
            padding: rem(4),
            background: '#F5F5F5',
            borderRadius: rem(16),
            width: 'fit-content',
            flexWrap: 'wrap'
          }}>
            <button
              onClick={() => setShowForm(true)}
              style={{
                padding: `${rem(10)} ${rem(20)}`,
                background: 'linear-gradient(to right, #6B7FFF, #8B5CF6)',
                color: '#FFFFFF',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: 600,
                boxShadow: '0 2px 8px rgba(107, 127, 255, 0.35)',
                transition: 'all 0.2s',
                display: 'flex',
                alignItems: 'center',
                gap: rem(6)
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.boxShadow = '0 4px 12px rgba(107, 127, 255, 0.45)';
                e.currentTarget.style.transform = 'translateY(-1px)';
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.boxShadow = '0 2px 8px rgba(107, 127, 255, 0.35)';
                e.currentTarget.style.transform = 'translateY(0)';
              }}
            >
              <svg width="16" height="16" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
              </svg>
              Dodaj leczenie
            </button>
            <button
              onClick={() => setCategoryFilter('ALL')}
              style={{
                padding: `${rem(10)} ${rem(20)}`,
                background: categoryFilter === 'ALL' ? '#FFFFFF' : 'transparent',
                color: categoryFilter === 'ALL' ? '#1A1A1A' : '#666666',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: categoryFilter === 'ALL' ? 600 : 400,
                boxShadow: categoryFilter === 'ALL' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Wszystkie ({myMedications.length})
            </button>
            <button
              onClick={() => setCategoryFilter('LEK')}
              style={{
                padding: `${rem(10)} ${rem(20)}`,
                background: categoryFilter === 'LEK' ? '#FFFFFF' : 'transparent',
                color: categoryFilter === 'LEK' ? '#1A1A1A' : '#666666',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: categoryFilter === 'LEK' ? 600 : 400,
                boxShadow: categoryFilter === 'LEK' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Leki ({myMedications.filter(m => m.category === 'LEK').length})
            </button>
            <button
              onClick={() => setCategoryFilter('SUPLEMENT')}
              style={{
                padding: `${rem(10)} ${rem(20)}`,
                background: categoryFilter === 'SUPLEMENT' ? '#FFFFFF' : 'transparent',
                color: categoryFilter === 'SUPLEMENT' ? '#1A1A1A' : '#666666',
                border: 'none',
                borderRadius: rem(12),
                cursor: 'pointer',
                fontSize: fontSize.sm,
                fontWeight: categoryFilter === 'SUPLEMENT' ? 600 : 400,
                boxShadow: categoryFilter === 'SUPLEMENT' ? '0 1px 3px rgba(0,0,0,0.1)' : 'none',
                transition: 'all 0.2s'
              }}
            >
              Suplementy ({myMedications.filter(m => m.category === 'SUPLEMENT').length})
            </button>
          </div>

          {/* Historia wpływu button */}
          {userId && (
            <div style={{ marginBottom: rem(24) }}>
              <button
                onClick={() => setShowHistory(!showHistory)}
                style={{
                  padding: `${rem(12)} ${rem(20)}`,
                  background: showHistory ? 'linear-gradient(to right, #6B7FFF, #8B5CF6)' : '#FFFFFF',
                  color: showHistory ? '#FFFFFF' : '#6366F1',
                  border: showHistory ? 'none' : '1px solid #E0E7FF',
                  borderRadius: rem(16),
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: 600,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(8),
                  transition: 'all 0.2s'
                }}
              >
                <svg width="18" height="18" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 19v-6a2 2 0 00-2-2H5a2 2 0 00-2 2v6a2 2 0 002 2h2a2 2 0 002-2zm0 0V9a2 2 0 012-2h2a2 2 0 012 2v10m-6 0a2 2 0 002 2h2a2 2 0 002-2m0 0V5a2 2 0 012-2h2a2 2 0 012 2v14a2 2 0 01-2 2h-2a2 2 0 01-2-2z" />
                </svg>
                {showHistory ? 'Ukryj' : 'Pokaż'} historię wpływu {categoryFilter === 'LEK' ? 'leczenia' : categoryFilter === 'SUPLEMENT' ? 'suplementów' : 'leczenia i suplementów'}
              </button>
            </div>
          )}

          {/* Historia wpływu - conditional rendering */}
          {showHistory && userId && (
            <div style={{ marginBottom: rem(32) }}>
              {categoryFilter === 'LEK' && <MedicationHistoryView userId={userId} />}
              {categoryFilter === 'SUPLEMENT' && <SupplementHistoryView userId={userId} />}
              {categoryFilter === 'ALL' && (
                <>
                  <MedicationHistoryView userId={userId} />
                  <div style={{ marginTop: rem(24) }}>
                    <SupplementHistoryView userId={userId} />
                  </div>
                </>
              )}
            </div>
          )}

          {/* Filtry */}
          {!showHistory && (
            <div style={{
              display: 'flex',
              gap: rem(8),
              marginBottom: rem(24),
              paddingBottom: rem(16),
              borderBottom: '1px solid #E5E5E5'
            }}>
              <button
                onClick={() => setListFilter('all')}
                style={{
                  padding: `${rem(8)} ${rem(16)}`,
                  background: listFilter === 'all' ? '#9C27B0' : 'transparent',
                  color: listFilter === 'all' ? '#FFFFFF' : '#666666',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: listFilter === 'all' ? 600 : 400,
                  transition: 'all 0.2s'
                }}
              >
                Wszystkie ({allActiveMedications.length})
              </button>
              <button
                onClick={() => setListFilter('permanent')}
                style={{
                  padding: `${rem(8)} ${rem(16)}`,
                  background: listFilter === 'permanent' ? '#9C27B0' : 'transparent',
                  color: listFilter === 'permanent' ? '#FFFFFF' : '#666666',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: listFilter === 'permanent' ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(6),
                  transition: 'all 0.2s'
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
                </svg>
                Na stałe ({permanentMedications.length})
              </button>
              <button
                onClick={() => setListFilter('temporary')}
                style={{
                  padding: `${rem(8)} ${rem(16)}`,
                  background: listFilter === 'temporary' ? '#9C27B0' : 'transparent',
                  color: listFilter === 'temporary' ? '#FFFFFF' : '#666666',
                  border: 'none',
                  borderRadius: rem(12),
                  cursor: 'pointer',
                  fontSize: fontSize.sm,
                  fontWeight: listFilter === 'temporary' ? 600 : 400,
                  display: 'flex',
                  alignItems: 'center',
                  gap: rem(6),
                  transition: 'all 0.2s'
                }}
              >
                <svg width="14" height="14" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4l3 3m6-3a9 9 0 11-18 0 9 9 0 0118 0z" />
                </svg>
                Obecne leczenie ({temporaryMedications.length})
              </button>
            </div>
          )}

          {/* Lista leków */}
          {!showHistory && (
            <>
              {listFilter === 'all' && (
                <>
                  {allActiveMedications.length === 0 ? (
                    <div style={{
                      padding: rem(60),
                      textAlign: 'center',
                      background: '#FAFAFA',
                      borderRadius: rem(24),
                      border: '1px solid #E5E5E5'
                    }}>
                      <p style={{
                        fontSize: fontSize.lg,
                        color: '#666666',
                        marginBottom: rem(8)
                      }}>
                        Brak aktywnych planów leczenia
                      </p>
                      <p style={{
                        fontSize: fontSize.sm,
                        color: '#999999'
                      }}>
                        Dodaj swój pierwszy plan leczenia, aby rozpocząć
                      </p>
                    </div>
                  ) : (
                    allActiveMedications.map(renderMedicationCard)
                  )}
                </>
              )}

              {listFilter === 'permanent' && (
                <>
                  {permanentMedications.length === 0 ? (
                    <div style={{
                      padding: rem(60),
                      textAlign: 'center',
                      background: '#FAFAFA',
                      borderRadius: rem(24),
                      border: '1px solid #E5E5E5'
                    }}>
                      <p style={{
                        fontSize: fontSize.lg,
                        color: '#666666',
                        marginBottom: rem(8)
                      }}>
                        Brak leków na stałe
                      </p>
                      <p style={{
                        fontSize: fontSize.sm,
                        color: '#999999'
                      }}>
                        Dodaj leki, które przyjmujesz ciągle
                      </p>
                    </div>
                  ) : (
                    permanentMedications.map(renderMedicationCard)
                  )}
                </>
              )}

              {listFilter === 'temporary' && (
                <>
                  {temporaryMedications.length === 0 ? (
                    <div style={{
                      padding: rem(60),
                      textAlign: 'center',
                      background: '#FAFAFA',
                      borderRadius: rem(24),
                      border: '1px solid #E5E5E5'
                    }}>
                      <p style={{
                        fontSize: fontSize.lg,
                        color: '#666666',
                        marginBottom: rem(8)
                      }}>
                        Brak obecnych planów leczenia
                      </p>
                      <p style={{
                        fontSize: fontSize.sm,
                        color: '#999999'
                      }}>
                        Dodaj leczenie czasowe, schodzenie z dawki lub leki jednorazowe
                      </p>
                    </div>
                  ) : (
                    temporaryMedications.map(renderMedicationCard)
                  )}
                </>
              )}

              {/* Nieaktywne leki */}
              {inactiveMedications.length > 0 && (
                <div style={{
                  marginTop: rem(32),
                  padding: rem(24),
                  background: '#FAFAFA',
                  borderRadius: rem(24),
                  border: '1px solid #E5E5E5'
                }}>
                  <h2 style={{
                    marginTop: 0,
                    fontSize: fontSize.lg,
                    color: '#666666',
                    marginBottom: rem(16)
                  }}>
                    Zakończone leczenie ({inactiveMedications.length})
                  </h2>
                  {inactiveMedications.map(medication => (
                    <div
                      key={medication.id}
                      style={{
                        padding: rem(16),
                        marginBottom: rem(12),
                        background: '#FFFFFF',
                        border: '1px solid #E5E5E5',
                        borderRadius: rem(16),
                        opacity: 0.7
                      }}
                    >
                      <div style={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        alignItems: 'center'
                      }}>
                        <div>
                          <strong style={{ fontSize: fontSize.base }}>
                            {medication.name}
                          </strong>
                          <span style={{
                            marginLeft: rem(8),
                            fontSize: fontSize.xs,
                            color: '#666666'
                          }}>
                            ({new Date(medication.startDate + 'T12:00:00').toLocaleDateString('pl-PL')}
                            {medication.endDate ? ` - ${new Date(medication.endDate + 'T12:00:00').toLocaleDateString('pl-PL')}` : ''})
                          </span>
                        </div>
                        <button
                          onClick={() => handleDelete(medication.id)}
                          style={{
                            padding: `${rem(6)} ${rem(12)}`,
                            background: '#EF4444',
                            color: 'white',
                            border: 'none',
                            borderRadius: rem(8),
                            cursor: 'pointer',
                            fontSize: fontSize.xs,
                            fontWeight: 600
                          }}
                        >
                          Usuń
                        </button>
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </>
          )}
        </>
      )}

    </div>
  );
};
